import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Check, Target, Zap } from 'lucide-react';
import { BEHAVIOUR_TAGS } from '../../utils/behaviourTags';
import { formatDelta, formatTarget, movementSense } from '../../utils/simulationEngine';

/**
 * The closing debrief on the room screen, after the last round.
 *
 * Three parts, stepped through by the trainer from the console:
 *   rounds    — round by round: what each team chose, and what that moved
 *   overall   — where every metric ended up (against target), and each team's total effect
 *   learnings — the teaching points, the insight on every choice, the cooperation trend
 *
 * There is deliberately no ranking: the teams are departments of one organisation and
 * share one set of numbers. Ranking them would reward protecting your own number —
 * the exact behaviour the simulation is built to surface.
 *
 * Dark and large: this is read from the back of a training room.
 */

export const FINAL_VIEWS = [
  { key: 'rounds', label: 'Round by round' },
  { key: 'overall', label: 'Overall' },
  { key: 'learnings', label: 'What we learned' },
];

const senseClass = (sense) => (
  sense === 'good' ? 'text-emerald-400' : sense === 'bad' ? 'text-red-400' : 'text-neutral-500'
);

/** The metrics a vector actually moved, as coloured "+5 Downtime" chips. */
const Moves = ({ kpis, vector, size = 'base' }) => {
  const moved = kpis.filter((k) => vector?.[k.key]);
  if (!moved.length) return <p className={clsx('text-neutral-600', size === 'lg' ? 'text-lg' : 'text-sm')}>No change</p>;
  return (
    <div className={clsx('flex flex-wrap gap-x-3 gap-y-1', size === 'lg' ? 'text-lg' : 'text-sm')}>
      {moved.map((k) => (
        <span key={k.key} className="whitespace-nowrap">
          <span className={clsx('font-bold tabular-nums', senseClass(movementSense(k, vector[k.key])))}>
            {formatDelta(vector[k.key])}
          </span>
          <span className="text-neutral-400"> {k.label}</span>
        </span>
      ))}
    </div>
  );
};

Moves.propTypes = { kpis: PropTypes.array.isRequired, vector: PropTypes.object, size: PropTypes.string };

const RoundByRound = ({ final, kpis }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[720px] border-separate border-spacing-2 text-left">
      <thead>
        <tr>
          <th className="w-48" />
          {final.rounds.map((r) => (
            <th key={r.index} className="rounded-2xl bg-neutral-900 px-5 py-4 align-top">
              <p className="text-sm font-bold uppercase tracking-wider text-primary-400">Round {r.index}</p>
              <p className="mt-1 text-xl font-bold leading-snug text-white">{r.title}</p>
              {r.event && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400">
                  <Zap size={14} strokeWidth={2} />{r.event.title}
                </p>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {final.rounds[0]?.teams.map((t, row) => (
          <tr key={t.functionKey}>
            <th className="px-3 py-4 align-top text-xl font-bold text-white">{t.name}</th>
            {final.rounds.map((r) => {
              const cell = r.teams[row];
              return (
                <td key={r.index} className="rounded-2xl bg-neutral-900/70 px-5 py-4 align-top">
                  <p className="text-lg font-semibold leading-snug text-neutral-100">{cell?.cardTitle || '—'}</p>
                  <div className="mt-2"><Moves kpis={kpis} vector={cell?.kpi} /></div>
                </td>
              );
            })}
          </tr>
        ))}
        <tr>
          <th className="px-3 py-4 align-top text-lg font-bold uppercase tracking-wider text-neutral-400">Round result</th>
          {final.rounds.map((r) => (
            <td key={r.index} className="rounded-2xl border-2 border-neutral-800 px-5 py-4 align-top">
              <Moves kpis={kpis} vector={r.total} />
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

RoundByRound.propTypes = { final: PropTypes.object.isRequired, kpis: PropTypes.array.isRequired };

const Overall = ({ final, kpis }) => {
  const byKey = Object.fromEntries((final.overall || []).map((o) => [o.key, o]));
  const anyEvents = kpis.some((k) => final.eventsTotal?.[k.key]);

  return (
    <div className="space-y-10">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map((k) => {
          const o = byKey[k.key] || { change: 0, level: 0, targetStatus: null };
          const target = formatTarget(k);
          return (
            <div
              key={k.key}
              className={clsx(
                'rounded-2xl border-2 px-6 py-5',
                o.targetStatus === 'met' ? 'border-emerald-500/50 bg-emerald-500/10'
                  : o.targetStatus === 'missed' ? 'border-amber-500/40 bg-amber-500/5'
                  : 'border-neutral-800 bg-neutral-900'
              )}
            >
              <p className="text-sm font-semibold uppercase tracking-wider text-neutral-400">{k.label}</p>
              <p className="mt-2 text-5xl font-bold tabular-nums text-white">{o.level}</p>
              <p className="mt-1 text-base text-neutral-500">
                {k.unit}{' '}
                <span className={clsx('font-bold', senseClass(movementSense(k, o.change)))}>{formatDelta(o.change)}</span>
              </p>
              {target && (
                <p className={clsx(
                  'mt-3 inline-flex items-center gap-1.5 text-lg font-semibold',
                  o.targetStatus === 'met' ? 'text-emerald-400' : 'text-amber-400'
                )}
                >
                  {o.targetStatus === 'met' ? <Check size={18} strokeWidth={3} /> : <Target size={18} strokeWidth={2} />}
                  Target {target} · {o.targetStatus === 'met' ? 'reached' : 'missed'}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="text-2xl font-bold text-white">What each team did to the numbers, across every round</h3>
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[640px] border-separate border-spacing-2 text-left">
            <thead>
              <tr>
                <th className="w-48" />
                {kpis.map((k) => (
                  <th key={k.key} className="px-4 py-2 text-sm font-semibold uppercase tracking-wider text-neutral-400">{k.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(final.teamTotals || []).map((t) => (
                <tr key={t.functionKey}>
                  <th className="rounded-xl bg-neutral-900 px-4 py-3 text-xl font-bold">{t.name}</th>
                  {kpis.map((k) => (
                    <td key={k.key} className={clsx('rounded-xl bg-neutral-900/70 px-4 py-3 text-2xl font-bold tabular-nums', senseClass(movementSense(k, t.kpi[k.key])))}>
                      {formatDelta(t.kpi[k.key])}
                    </td>
                  ))}
                </tr>
              ))}
              {anyEvents && (
                <tr>
                  <th className="rounded-xl px-4 py-3 text-lg font-semibold text-amber-400">Outside events</th>
                  {kpis.map((k) => (
                    <td key={k.key} className={clsx('rounded-xl px-4 py-3 text-xl font-bold tabular-nums', senseClass(movementSense(k, final.eventsTotal[k.key])))}>
                      {formatDelta(final.eventsTotal[k.key])}
                    </td>
                  ))}
                </tr>
              )}
              <tr>
                <th className="rounded-xl border-2 border-neutral-800 px-4 py-3 text-lg font-bold uppercase tracking-wider text-neutral-300">Together</th>
                {kpis.map((k) => (
                  <td key={k.key} className={clsx('rounded-xl border-2 border-neutral-800 px-4 py-3 text-2xl font-bold tabular-nums', senseClass(movementSense(k, final.cumulative?.[k.key])))}>
                    {formatDelta(final.cumulative?.[k.key])}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-lg text-neutral-500">
          No team wins. Every team&apos;s numbers add up to the same organisation&apos;s result.
        </p>
      </div>
    </div>
  );
};

Overall.propTypes = { final: PropTypes.object.isRequired, kpis: PropTypes.array.isRequired };

const Learnings = ({ final }) => {
  const { teachingPoints = [], choices = [], cooperationTrend = [], archetypeMix = [] } = final.learnings || {};

  return (
    <div className="grid gap-10 lg:grid-cols-[3fr_2fr]">
      <div className="space-y-8">
        {teachingPoints.length > 0 && (
          <div className="space-y-4">
            {teachingPoints.map((t) => (
              <div key={t.index} className="rounded-2xl border-l-4 border-primary-500 bg-neutral-900 px-7 py-5">
                <p className="text-sm font-bold uppercase tracking-wider text-primary-400">Round {t.index} · {t.title}</p>
                <p className="mt-2 text-2xl leading-relaxed text-white">{t.teachingPoint}</p>
              </div>
            ))}
          </div>
        )}

        <div>
          <h3 className="text-2xl font-bold text-white">What the choices said</h3>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {choices.map((c) => (
              <div key={c.functionKey} className="rounded-2xl bg-neutral-900 px-6 py-5">
                <p className="text-sm font-bold uppercase tracking-wider text-primary-400">{c.name}</p>
                <ul className="mt-3 space-y-3">
                  {c.picks.filter((p) => p.cardTitle).map((p) => (
                    <li key={p.roundIndex}>
                      <p className="text-base text-neutral-400">Round {p.roundIndex}: {p.cardTitle}</p>
                      {p.leadershipAngle && <p className="text-xl font-semibold text-neutral-100">{p.leadershipAngle}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {cooperationTrend.length > 0 && (
          <div className="rounded-2xl bg-neutral-900 px-6 py-5">
            <h3 className="text-xl font-bold text-white">Cooperation, round by round</h3>
            <div className="mt-5 space-y-4">
              {cooperationTrend.map((t) => (
                <div key={t.round} className="flex items-center gap-4">
                  <span className="w-24 shrink-0 text-lg text-neutral-400">Round {t.round}</span>
                  <div className="h-4 flex-1 overflow-hidden rounded-full bg-neutral-800">
                    <div className="h-full rounded-full bg-primary-500" style={{ width: `${Math.max(0, Math.min(100, t.cooperation))}%` }} />
                  </div>
                  <span className="w-16 shrink-0 text-right text-xl font-bold tabular-nums">{t.cooperation}%</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-base text-neutral-500">A dip is the moment the room started protecting its own numbers.</p>
          </div>
        )}

        {archetypeMix.length > 0 && (
          <div className="rounded-2xl bg-neutral-900 px-6 py-5">
            <h3 className="text-xl font-bold text-white">How the room behaved</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {archetypeMix.map((m) => (
                <span key={m.tag} className="rounded-xl bg-neutral-800 px-3 py-1.5 text-lg">
                  {BEHAVIOUR_TAGS[m.tag]?.label || m.tag}
                  <span className="ml-1.5 font-bold tabular-nums text-neutral-400">×{m.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

Learnings.propTypes = { final: PropTypes.object.isRequired };

const FinalDebrief = ({ final, kpis, view = 'rounds' }) => {
  if (!final?.rounds?.length) {
    return <p className="text-3xl text-neutral-400">No rounds were played.</p>;
  }

  return (
    <div>
      {/* Where we are in the debrief. Not buttons: the projector has no controls — the
          trainer moves it on from the console. */}
      <div className="mb-8 flex flex-wrap gap-2">
        {FINAL_VIEWS.map((v) => (
          <span
            key={v.key}
            className={clsx(
              'rounded-xl px-4 py-2 text-lg font-semibold',
              v.key === view ? 'bg-primary-500 text-white' : 'bg-neutral-900 text-neutral-500'
            )}
          >
            {v.label}
          </span>
        ))}
      </div>

      {view === 'overall' && <Overall final={final} kpis={kpis} />}
      {view === 'learnings' && <Learnings final={final} />}
      {view !== 'overall' && view !== 'learnings' && <RoundByRound final={final} kpis={kpis} />}
    </div>
  );
};

FinalDebrief.propTypes = {
  final: PropTypes.object,
  kpis: PropTypes.arrayOf(PropTypes.object).isRequired,
  view: PropTypes.oneOf(['rounds', 'overall', 'learnings']),
};

export default FinalDebrief;
